import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { isWidgetLoaded, removeWidgetAndReload } from "@/lib/widget";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(undefined);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch {
            setUser(null);
            return null;
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        const expired = () => setUser((u) => (u ? null : u));
        const mustChange = () => setUser((u) => (u ? { ...u, must_change_password: true } : u));
        window.addEventListener("edi:unauthorized", expired);
        window.addEventListener("edi:password-change", mustChange);
        return () => { window.removeEventListener("edi:unauthorized", expired); window.removeEventListener("edi:password-change", mustChange); };
    }, []);

    const login = useCallback(async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        setUser(data.user);
        return data.user;
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } finally {
            if (isWidgetLoaded()) removeWidgetAndReload("/accedi");
            setUser(null);
        }
    }, []);

    const value = useMemo(() => ({ user, setUser, refresh, login, logout }), [user, refresh, login, logout]);
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);

export const homeForRole = (role) => (role === "azienda" ? "/profilo" : "/amministrazione");
